// src/index.ts
import MagicString from "magic-string";
import escapeHtml from "escape-html";
import { parseDocument, DomUtils } from "@customerio/source-htmlparser2";
import { select } from "cheerio-select";
var MagicHtml = class {
  __source;
  __s;
  __dom;
  __flushed = false;
  __MagicHtml;
  __MagicElement;
  constructor(source, options) {
    this.__source = source;
    this.__s = new MagicString(source);
    this.__dom = parseDocument(source, {
      recognizeSelfClosing: true,
      ...options
    });
    this.__flushed = true;
    this.__MagicElement = options?.MagicElement || MagicElement;
    this.__MagicHtml = MagicHtml;
  }
  trim(charType) {
    this.__s.trim(charType);
    this.__flushed = false;
    return this;
  }
  trimStart(charType) {
    this.__s.trimStart(charType);
    this.__flushed = false;
    return this;
  }
  trimEnd(charType) {
    this.__s.trimEnd(charType);
    this.__flushed = false;
    return this;
  }
  trimLines() {
    this.__s.trimLines();
    this.__flushed = false;
    return this;
  }
  isEmpty() {
    return this.__s.isEmpty();
  }
  isFlushed() {
    return this.__flushed;
  }
  generateDecodedMap(options) {
    return this.__s.generateDecodedMap(options);
  }
  generateMap(options) {
    return this.__s.generateMap(options);
  }
  toString() {
    return this.__s.toString();
  }
  clone() {
    return new MagicHtml(this.__s.toString());
  }
  flush(source) {
    this.__source = source ?? this.__s.toString();
    this.__s = new MagicString(this.__source);
    this.__dom = parseDocument(this.__source);
    this.__flushed = true;
    return this;
  }
  querySelector(selector) {
    const result = select(selector, this.__dom)?.[0];
    if (!result) {
      return null;
    }
    return new this.__MagicElement(result, this);
  }
  querySelectorAll(selector) {
    return select(selector, this.__dom).map((element) => {
      return new this.__MagicElement(element, this);
    });
  }
};
var MagicElement = class {
  __element;
  __magicHtml;
  __isClone = false;
  constructor(element, magicHtml) {
    this.__element = element;
    this.__magicHtml = magicHtml;
  }
  get tagName() {
    return this.__element.tagName;
  }
  set tagName(tagName) {
    if (!this.__element.endIndex) {
      return;
    }
    tagName = tagName.toLowerCase();
    const currentTagName = this.__element.tagName;
    this.__magicHtml.__s.overwrite(
      this.__element.source.openTag.startIndex + 1,
      this.__element.source.openTag.startIndex + 1 + currentTagName.length,
      tagName
    );
    if (this.__element.source.closeTag) {
      this.__magicHtml.__s.overwrite(
        this.__element.source.closeTag.startIndex + 2,
        this.__element.source.closeTag.startIndex + 2 + currentTagName.length,
        tagName
      );
    }
    this.__magicHtml.__flushed = false;
  }
  get id() {
    return this.__element.attribs.id ?? "";
  }
  get classList() {
    const classList = (this.__element.attribs.class ?? "").split(" ").map((c) => c.trim());
    return classList.filter((c) => Boolean(c.trim()));
  }
  get className() {
    return this.__element.attribs.class ?? "";
  }
  get attributes() {
    return this.__element.source.attributes.map((attr) => {
      return {
        name: attr.name.data,
        value: attr.value.data
      };
    });
  }
  get innerHTML() {
    if (!this.__element.endIndex) {
      return "";
    }
    return this.__magicHtml.__source.slice(
      this.__element.source.openTag.endIndex + 1,
      this.__element?.source?.closeTag?.startIndex ?? this.__element.endIndex
    );
  }
  set innerHTML(html) {
    if (!this.__element.endIndex) {
      return;
    }
    if (this.innerHTML.length === 0) {
      this.__magicHtml.__s.appendRight(this.__element.source.openTag.endIndex + 1, html);
    } else {
      this.__magicHtml.__s.overwrite(
        this.__element.source.openTag.endIndex + 1,
        this.__element?.source?.closeTag?.startIndex ?? this.__element.endIndex,
        html
      );
    }
    this.__magicHtml.__flushed = false;
  }
  get textContent() {
    return DomUtils.textContent(this.__element);
  }
  set textContent(text) {
    if (!this.__element.endIndex) {
      return;
    }
    this.innerHTML = escapeHtml(text);
  }
  get outerHTML() {
    return this.__magicHtml.__source.slice(
      this.__element.source.openTag.startIndex,
      this.__element.source.closeTag?.endIndex ?? this.__element.endIndex + 1
    );
  }
  get children() {
    return this.__element.children;
  }
  get parent() {
    const { parent } = this.__element;
    if (parent?.type === "tag") {
      return new this.__magicHtml.__MagicElement(parent, this.__magicHtml);
    }
    return null;
  }
  before(html) {
    this.__magicHtml.__s.prependLeft(this.__element.source.openTag.startIndex, html);
    this.__magicHtml.__flushed = false;
    return this;
  }
  after(html) {
    this.__magicHtml.__s.appendRight(this.__element.source.closeTag?.endIndex ?? this.__element.endIndex + 1, html);
    this.__magicHtml.__flushed = false;
    return this;
  }
  prepend(html) {
    if (this.__element.source.openTag.isSelfClosing) {
      const hasSlash = this.__magicHtml.__source.charAt(this.__element.source.openTag.endIndex - 1) === "/";
      if (hasSlash) {
        this.__magicHtml.__s.remove(this.__element.source.openTag.endIndex - 1, this.__element.source.openTag.endIndex);
      }
    }
    this.__magicHtml.__s.prependLeft(this.__element.source.openTag.endIndex + 1, html);
    if (this.__element.source.openTag.isSelfClosing) {
      this.__magicHtml.__s.appendRight(this.__element.source.openTag.endIndex + 1, `</${this.__element.tagName}>`);
    }
    this.__magicHtml.__flushed = false;
    return this;
  }
  append(html) {
    if (this.__element.source.openTag.isSelfClosing) {
      return this.prepend(html);
    }
    this.__magicHtml.__s.appendRight(this.__element?.source?.closeTag?.startIndex ?? this.__element.endIndex, html);
    this.__magicHtml.__flushed = false;
    return this;
  }
  remove() {
    this.__magicHtml.__s.remove(
      this.__element.source.openTag.startIndex,
      // if the item we are removing is the last item in the document,
      // the +1 will cause an out of bounds error so we make sure
      // we don't go past the end of the document
      Math.min(
        this.__element.source.closeTag?.endIndex ?? this.__element.endIndex + 1,
        this.__magicHtml.__source.length
      )
    );
    this.__magicHtml.__flushed = false;
    return this;
  }
  replaceWith(html) {
    this.__magicHtml.__s.overwrite(
      this.__element.source.openTag.startIndex,
      // if the item we are replacinng is the last item in the document,
      // the +1 will cause an out of bounds error so we make sure
      // we don't go past the end of the document
      Math.min(
        this.__element.source.closeTag?.endIndex ?? this.__element.endIndex + 1,
        this.__magicHtml.__source.length
      ),
      html
    );
    this.__magicHtml.__flushed = false;
    return this;
  }
  hasAttribute(name) {
    return name in this.__element.attribs;
  }
  hasAttributes() {
    return Object.keys(this.__element.attribs).length > 0;
  }
  getAttribute(name) {
    return this.__element.attribs[name] ?? null;
  }
  getAttributeNames() {
    return Object.keys(this.__element.attribs);
  }
  setAttribute(name, value) {
    const valueNeedsQuotes = /["'`=<>\s]/.test(value);
    const attribute = this.__element.source.attributes.find((a) => a.name.data === name);
    const escapedValue = !valueNeedsQuotes ? value : attribute?.quote === "'" ? value.replace(/'/g, "&#39;") : value.replace(/"/g, "&quot;");
    if (attribute) {
      if (attribute?.value && attribute.value.startIndex <= attribute.value.endIndex) {
        this.__magicHtml.__s.overwrite(
          attribute.value?.startIndex,
          attribute.value?.endIndex + 1,
          valueNeedsQuotes && !attribute.quote ? `"${escapedValue}"` : escapedValue
        );
      } else if (
        /**
         * The value is empty so we need to add it
         */
        attribute?.value && attribute.value.startIndex > attribute.value.endIndex && attribute.value.data === ""
      ) {
        this.__magicHtml.__s.appendRight(
          attribute.value.startIndex,
          valueNeedsQuotes && !attribute.quote ? `"${escapedValue}"` : escapedValue
        );
      } else {
        this.__magicHtml.__s.appendRight(attribute.name.endIndex + 1, `="${escapedValue}"`);
      }
    } else {
      this.__magicHtml.__s.appendRight(
        this.__element.source.openTag.startIndex + this.__element.tagName.length + 1,
        // +1 for the <
        ` ${name}="${escapedValue}"`
      );
    }
    this.__magicHtml.__flushed = false;
    return this;
  }
  toggleAttribute(name, force) {
    if (force === true) {
      this.setAttribute(name, "");
    } else if (force === false) {
      this.removeAttribute(name);
    } else {
      if (this.hasAttribute(name)) {
        this.removeAttribute(name);
      } else {
        this.setAttribute(name, "");
      }
    }
    this.__magicHtml.__flushed = false;
    return this;
  }
  removeAttribute(name) {
    const remainingAttributesCount = this.__element.source.attributes.filter((a) => a.name.data !== name).length;
    for (const [index, attribute] of this.__element.source.attributes.entries()) {
      if (attribute.name.data !== name) {
        continue;
      }
      const isLastAttribute = attribute === this.__element.source.attributes[this.__element.source.attributes.length - 1];
      const isPreviousRemoved = index > 0 && this.__element.source.attributes[index - 1].name.data === name;
      this.__magicHtml.__s.remove(
        attribute.source.startIndex - (remainingAttributesCount === 0 ? 1 : isLastAttribute ? 1 : isPreviousRemoved ? 1 : 0),
        attribute.source.endIndex + 1
      );
    }
    this.__magicHtml.__flushed = false;
    return this;
  }
  querySelector(selector) {
    const result = select(selector, this.__element)?.[0] ?? null;
    if (!result) {
      return null;
    }
    return new this.__magicHtml.__MagicElement(result, this.__magicHtml);
  }
  querySelectorAll(selector) {
    return select(selector, this.__element).map((element) => {
      return new this.__magicHtml.__MagicElement(element, this.__magicHtml);
    });
  }
  toString() {
    if (this.__isClone) {
      return this.__magicHtml.toString();
    }
    return this.outerHTML;
  }
  clone() {
    const MagicHtml2 = this.__magicHtml.__MagicHtml;
    const clone = new MagicHtml2(this.outerHTML).querySelector("*");
    clone.__isClone = true;
    return clone;
  }
};
export {
  MagicElement,
  MagicHtml
};
